import { useEffect, useState } from "react";
import { Smartphone, Download, Share, Plus } from "lucide-react";

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari quirk
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true);

const isIOS = () =>
  typeof window !== "undefined" &&
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
  !(window as unknown as { MSStream?: unknown }).MSStream;

/**
 * Landing-page band promoting PWA install. Sits between hero and
 * "The Problem" section. Renders nothing when the app is already
 * installed (running in standalone mode).
 */
export default function InstallBand() {
  const [deferredPrompt, setDeferredPrompt] = useState<BIPEvent | null>(null);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setHidden(true);
      return;
    }
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BIPEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden || installed) return null;

  const onClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (isIOS()) {
      setShowIOSHelp((v) => !v);
      return;
    }
    // Desktop Chrome/Edge fallback: open Chrome menu hint
    setShowIOSHelp((v) => !v);
  };

  const ctaText = isIOS() ? "Add to Home Screen" : "Install Time Miners";

  return (
    <section
      className="tm-section"
      style={{ paddingTop: "2.5rem", paddingBottom: "2.5rem" }}
      aria-label="Install Time Miners on your device"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "2rem",
          padding: "1.5rem 2rem",
          borderRadius: "20px",
          background:
            "linear-gradient(135deg, rgba(201,169,110,0.08) 0%, rgba(201,169,110,0.02) 100%)",
          border: "1px solid rgba(201,169,110,0.25)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1.25rem", flex: "1 1 320px" }}>
          <div
            style={{
              position: "relative",
              width: 56,
              height: 56,
              borderRadius: 14,
              background: "rgba(201,169,110,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <Smartphone className="" style={{ width: 28, height: 28, color: "#C9A96E" }} />
            <Download
              style={{
                position: "absolute",
                bottom: -4,
                right: -4,
                width: 18,
                height: 18,
                color: "#C9A96E",
                background: "#0A1020",
                padding: 2,
                borderRadius: 999,
              }}
            />
          </div>
          <div>
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#fff",
                margin: 0,
                marginBottom: "0.25rem",
                letterSpacing: "-0.01em",
              }}
            >
              Get Time Miners on your home screen
            </h2>
            <p style={{ color: "var(--text-dim, #8B96B5)", margin: 0, fontSize: "0.95rem" }}>
              One tap to install. Works offline. No app store needed.
            </p>
          </div>
        </div>
        <button
          onClick={onClick}
          style={{
            background: "#C9A96E",
            color: "#0A1020",
            fontWeight: 700,
            padding: "0.85rem 1.5rem",
            borderRadius: "12px",
            border: "none",
            cursor: "pointer",
            fontSize: "0.95rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            whiteSpace: "nowrap",
          }}
        >
          <Download style={{ width: 18, height: 18 }} />
          {ctaText}
        </button>
      </div>

      {showIOSHelp && (
        <div
          style={{
            marginTop: "1rem",
            padding: "1rem 1.25rem",
            borderRadius: "14px",
            background: "rgba(10, 16, 32, 0.6)",
            border: "1px solid rgba(201,169,110,0.2)",
            color: "var(--text-dim, #8B96B5)",
            fontSize: "0.9rem",
            lineHeight: 1.6,
          }}
          role="region"
          aria-label="Install instructions"
        >
          {isIOS() ? (
            <>
              <strong style={{ color: "#fff" }}>iPhone / iPad:</strong> Tap the{" "}
              <Share style={{ width: 14, height: 14, display: "inline", verticalAlign: "middle" }} />{" "}
              <span style={{ color: "#fff" }}>Share</span> button in Safari, scroll down, then tap{" "}
              <Plus style={{ width: 14, height: 14, display: "inline", verticalAlign: "middle" }} />{" "}
              <span style={{ color: "#fff" }}>Add to Home Screen</span>.
            </>
          ) : (
            <>
              <strong style={{ color: "#fff" }}>Install from your browser:</strong> Open the browser
              menu (⋮) and choose <span style={{ color: "#fff" }}>Install app</span> or{" "}
              <span style={{ color: "#fff" }}>Add to Home Screen</span>. Available on Chrome, Edge,
              Brave, and Samsung Internet.
            </>
          )}
        </div>
      )}
    </section>
  );
}
